import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getFullscreenPortalTarget } from './fullscreenPortal';
import { FaChevronLeft, FaChevronRight, FaVideo, FaVideoSlash, FaSpinner, FaTimes, FaVolumeUp, FaVolumeMute, FaUserCircle, FaEye, FaCheck } from 'react-icons/fa';
import { Stage, LocalStageStream, StageEvents, StreamType, SubscribeType } from 'amazon-ivs-web-broadcast';
import StagePlayer from './StagePlayer';
import virtualBgNeon from '../images/unite_banner.jpg';
import intersectBg from '../images/intersect-test-loading-05.png';
import API from '../../API';
import logger from '../../utils/logger';
import './UnityGame.css';
import AvatarFacePins, {
  getFacePinCapacity,
  participantIdentityKeys,
  selectVisibleFaces,
  type FaceLayoutEntry,
} from './AvatarFacePins';
import UnityGameDockApp from './UnityGameDockApp';

// Background effect types
type BackgroundEffect = 'none' | 'blur' | 'virtual-neon' | 'intersect-background';

interface Participant {
  id: string;
  userId: string;
  identityKeys?: string[];
  publishedTracks: any[];
  subscribedTracks: any[];
  isIntersectStaff?: boolean;
  intersectId?: string;
}

interface IVSRealtimePanelProps {
  accessToken?: string;
  userId?: string;
  isIntersectStaff?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
  onStageViewToggle?: (enabled: boolean) => void;
  /** @deprecated Retained for sandbox compatibility; the panel no longer renders a disconnect button. */
  onDisablePanel?: () => void;
  /** Publish-only screen share UI from `IvsScreenSharePublisherPanel` (optional). */
  screenShareControls?: React.ReactNode;
  /** True while local user is publishing screen share — extends grid tile count. */
  screenShareActive?: boolean;
  externalCollapseControl?: boolean;
  isCollapsedExternal?: boolean;
  roomName?: string;
  debugMode?: boolean;
  isLoading?: boolean;
  stagePlaybackUrl?: string;
  isStageViewActive?: boolean;
  isStageMuted?: boolean;
  onToggleStageMute?: () => void;
  // Video call specific props - for fetching video call token
  eventId?: string;
  roomId?: string;
  // Tour/onboarding identifier
  dataTourId?: string;
  /** Render remote camera feeds over their Unity avatars instead of in the side-panel grid. */
  inWorldVideoEnabled?: boolean;
}

const IVSRealtimePanel: React.FC<IVSRealtimePanelProps> = ({ 
  accessToken,
  userId,
  isIntersectStaff = false,
  onCollapseChange,
  onStageViewToggle,
  screenShareControls,
  screenShareActive = false,
  externalCollapseControl = false,
  isCollapsedExternal,
  roomName = 'Video Chat',
  debugMode = false,
  isLoading = false,
  stagePlaybackUrl = '',
  isStageViewActive = false,
  isStageMuted: externalIsStageMuted,
  onToggleStageMute: externalOnToggleStageMute,
  eventId,
  roomId,
  dataTourId,
  inWorldVideoEnabled = false
}) => {
  // logger.log('[IVS Panel] 🚀 COMPONENT MOUNTED/RENDERED', { 
  //   hasStageArn: !!stageArn, 
  //   hasParticipantToken: !!participantToken,
  //   userId,
  //   roomName 
  // });
  
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed
  const [debugParticipants, setDebugParticipants] = useState<Participant[]>([]);
  const debugParticipantVideoRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const [isStageMiniPlayerMinimized, setIsStageMiniPlayerMinimized] = useState(true); // Start minimized to save resources
  const [internalIsStageMuted, setInternalIsStageMuted] = useState(true); // Stage audio muted by default
  
  // Use external muted state if provided, otherwise use internal
  const isStageMuted = externalIsStageMuted !== undefined ? externalIsStageMuted : internalIsStageMuted;
  const handleToggleStageMute = externalOnToggleStageMute || (() => setInternalIsStageMuted(!internalIsStageMuted));
  
  // Sync with external collapse state when controlled externally
  useEffect(() => {
    if (externalCollapseControl && isCollapsedExternal !== undefined) {
      setIsCollapsed(isCollapsedExternal);
    }
  }, [externalCollapseControl, isCollapsedExternal]);
  
  const handleCollapseToggle = () => {
    if (externalCollapseControl) {
      // When externally controlled, just notify parent
      if (onCollapseChange) {
        onCollapseChange(!isCollapsed);
      }
    } else {
      // Internal control
      const newState = !isCollapsed;
      setIsCollapsed(newState);
      if (onCollapseChange) {
        onCollapseChange(newState);
      }
    }
  };
  
  // Debug mode functions
  const addDebugParticipant = () => {
    // Check if we're at the video limit
    const currentTotal = participantsWithStreams.length + (localVideoEnabled ? 1 : 0);
    if (currentTotal >= MAX_VIDEO_PARTICIPANTS) {
      logger.log('[Debug] Cannot add participant - video limit reached');
      return;
    }
    
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
    const names = ['Alice', 'Bob', 'Charlie', 'Diana', 'Eve', 'Frank', 'Grace', 'Henry', 'Iris', 'Jack'];
    const id = `debug-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const color = colors[debugParticipants.length % colors.length];
    const name = names[debugParticipants.length % names.length];
    const isStaff = Math.random() > 0.7; // 30% chance of being staff
    
    const newParticipant: Participant = {
      id,
      userId: name,
      publishedTracks: [],
      subscribedTracks: [],
      isIntersectStaff: isStaff
    };
    
    setDebugParticipants(prev => [...prev, newParticipant]);
    
    // Create a mock video stream using canvas
    setTimeout(() => {
      const canvas = debugParticipantVideoRefs.current.get(id);
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          canvas.width = 320;
          canvas.height = 180;
          
          // Animate the canvas to show it's "live"
          let frame = 0;
          const animate = () => {
            if (!debugParticipantVideoRefs.current.has(id)) return; // Stop if participant removed
            
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add some animation - moving circle
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            const x = (canvas.width / 2) + Math.sin(frame * 0.05) * 50;
            const y = (canvas.height / 2) + Math.cos(frame * 0.05) * 30;
            ctx.beginPath();
            ctx.arc(x, y, 20, 0, Math.PI * 2);
            ctx.fill();
            
            // Add participant name
            ctx.fillStyle = 'white';
            ctx.font = 'bold 24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(name, canvas.width / 2, canvas.height / 2);
            
            frame++;
            requestAnimationFrame(animate);
          };
          animate();
        }
      }
    }, 100);
  };
  
  const removeDebugParticipant = () => {
    if (debugParticipants.length === 0) return;
    const lastParticipant = debugParticipants[debugParticipants.length - 1];
    debugParticipantVideoRefs.current.delete(lastParticipant.id);
    setDebugParticipants(prev => prev.slice(0, -1));
  };
  
  const clearDebugParticipants = () => {
    debugParticipantVideoRefs.current.clear();
    setDebugParticipants([]);
  };

  // Load MediaPipe Image Segmenter from CDN
  const loadSegmenter = useCallback(async () => {
    if (segmenterRef.current) return segmenterRef.current;
    
    setIsSegmenterLoading(true);
    setSegmenterError(null);
    
    try {
      // Use dynamic import with a URL string (bypass TypeScript check)
      const cdnUrl = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/+esm';
      const module = await (Function('return import("' + cdnUrl + '")')() as Promise<any>);
      const { FilesetResolver, ImageSegmenter } = module;
      
      logger.log('[IVS Panel] MediaPipe Vision loaded from CDN');
      
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
      );
      
      const segmenter = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        outputCategoryMask: true,
        outputConfidenceMasks: false
      });
      
      segmenterRef.current = segmenter;
      logger.log('[IVS Panel] ✅ MediaPipe Segmenter initialized');
      setIsSegmenterLoading(false);
      return segmenter;
    } catch (error) {
      console.error('[IVS Panel] ❌ Failed to load segmenter:', error);
      setSegmenterError('Failed to load background blur. Try again.');
      setIsSegmenterLoading(false);
      return null;
    }
  }, []);

  // Process video frame with background effect (blur or virtual background)
  const processVideoFrame = useCallback((
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    offscreenCanvas: HTMLCanvasElement,
    segmenter: any,
    effect: BackgroundEffect,
    bgImage?: HTMLImageElement | null
  ) => {
    if (!video || !canvas || !segmenter || video.readyState < 2) return;
    
    const ctx = canvas.getContext('2d');
    const offCtx = offscreenCanvas.getContext('2d');
    if (!ctx || !offCtx) return;
    
    const width = video.videoWidth;
    const height = video.videoHeight;
    
    // Ensure canvas matches video dimensions
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      offscreenCanvas.width = width;
      offscreenCanvas.height = height;
    }
    
    try {
      // Run segmentation
      const startTimeMs = performance.now();
      const result = segmenter.segmentForVideo(video, startTimeMs);
      
      if (result && result.categoryMask) {
        const mask = result.categoryMask;
        
        // Step 1: Draw background to main canvas
        if (effect === 'blur') {
          // Blur the video for background
          ctx.filter = 'blur(12px)';
          ctx.drawImage(video, 0, 0, width, height);
          ctx.filter = 'none';
        } else if (bgImage && (effect === 'virtual-neon' || effect === 'intersect-background')) {
          // Draw virtual background image, scaled to cover
          const imgAspect = bgImage.width / bgImage.height;
          const canvasAspect = width / height;
          let drawWidth, drawHeight, drawX, drawY;
          
          if (imgAspect > canvasAspect) {
            drawHeight = height;
            drawWidth = height * imgAspect;
            drawX = (width - drawWidth) / 2;
            drawY = 0;
          } else {
            drawWidth = width;
            drawHeight = width / imgAspect;
            drawX = 0;
            drawY = (height - drawHeight) / 2;
          }
          
          ctx.drawImage(bgImage, drawX, drawY, drawWidth, drawHeight);
        } else {
          // Fallback to blur
          ctx.filter = 'blur(12px)';
          ctx.drawImage(video, 0, 0, width, height);
          ctx.filter = 'none';
        }
        
        // Step 2: Draw original video to offscreen canvas
        offCtx.drawImage(video, 0, 0, width, height);
        
        // Step 3: Create mask canvas for person silhouette
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        const maskCtx = maskCanvas.getContext('2d');
        
        if (maskCtx) {
          // Get mask data and create an image from it
          const maskData = mask.getAsUint8Array();
          const imageData = maskCtx.createImageData(width, height);
          
          // Convert mask to alpha channel (person = opaque, background = transparent)
          // MediaPipe selfie segmenter: low values = person, high values = background
          for (let i = 0; i < maskData.length; i++) {
            const pixelIndex = i * 4;
            const isPerson = maskData[i] < 128;
            imageData.data[pixelIndex] = 255;     // R
            imageData.data[pixelIndex + 1] = 255; // G
            imageData.data[pixelIndex + 2] = 255; // B
            imageData.data[pixelIndex + 3] = isPerson ? 255 : 0; // A - only show person
          }
          
          maskCtx.putImageData(imageData, 0, 0);
          
          // Step 4: Use mask to composite person on top of background
          offCtx.globalCompositeOperation = 'destination-in';
          offCtx.drawImage(maskCanvas, 0, 0);
          offCtx.globalCompositeOperation = 'source-over';
          
          // Step 5: Draw masked person over background
          ctx.drawImage(offscreenCanvas, 0, 0);
        }
        
        // Clean up mask
        mask.close();
      } else {
        // Fallback: just draw the video without effect
        ctx.drawImage(video, 0, 0, width, height);
      }
    } catch (error) {
      // On error, just draw the video without processing
      ctx.drawImage(video, 0, 0, width, height);
    }
  }, []);

  // Load virtual background image
  const loadVirtualBackground = useCallback((effect: BackgroundEffect): Promise<HTMLImageElement | null> => {
    return new Promise((resolve) => {
      if (effect === 'virtual-neon') {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = virtualBgNeon;
      } else if (effect === 'intersect-background') {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = intersectBg;
      } else {
        resolve(null);
      }
    });
  }, []);

  // Start background processing loop
  const startBackgroundProcessing = useCallback(async (
    rawStream: MediaStream,
    effect: BackgroundEffect
  ): Promise<MediaStream | null> => {
    if (effect === 'none') {
      return rawStream;
    }
    
    const segmenter = await loadSegmenter();
    if (!segmenter) {
      console.error('[IVS Panel] Segmenter not available, using raw stream');
      return rawStream;
    }
    
    // Load virtual background if needed
    let bgImage: HTMLImageElement | null = null;
    if (effect === 'virtual-neon' || effect === 'intersect-background') {
      bgImage = await loadVirtualBackground(effect);
      virtualBgImageRef.current = bgImage;
      logger.log('[IVS Panel] 🖼️ Virtual background loaded:', effect, !!bgImage);
    }
    
    // Create hidden video element for processing
    const video = document.createElement('video');
    video.srcObject = rawStream;
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    processingVideoRef.current = video;
    
    // Create canvases for processing
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 240;
    canvasRef.current = canvas;
    
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = 320;
    offscreenCanvas.height = 240;
    offscreenCanvasRef.current = offscreenCanvas;
    
    // Wait for video to be ready
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        offscreenCanvas.width = video.videoWidth;
        offscreenCanvas.height = video.videoHeight;
        video.play();
        resolve();
      };
    });
    
    // Start processing loop at ~15fps for IVS compatibility
    const targetFps = 15;
    let lastFrameTime = 0;
    const frameInterval = 1000 / targetFps;
    
    const processFrame = (currentTime: number) => {
      if (!processingVideoRef.current) {
        return;
      }
      
      // Process every frame for smooth streaming
      if (currentTime - lastFrameTime >= frameInterval) {
        processVideoFrame(video, canvas, offscreenCanvas, segmenter, effect, bgImage);
        lastFrameTime = currentTime;
      }
      
      animationFrameRef.current = requestAnimationFrame(processFrame);
    };
    
    animationFrameRef.current = requestAnimationFrame(processFrame);
    
    // Get stream from canvas - use higher fps for smoother remote viewing
    const processedStream = canvas.captureStream(targetFps);
    processedStreamRef.current = processedStream;
    
    // Ensure the video track has content before returning
    await new Promise<void>((resolve) => {
      const checkTrack = () => {
        const track = processedStream.getVideoTracks()[0];
        if (track && track.readyState === 'live') {
          resolve();
        } else {
          setTimeout(checkTrack, 50);
        }
      };
      setTimeout(checkTrack, 100);
    });
    
    logger.log('[IVS Panel] ✅ Background processing started with effect:', effect);
    return processedStream;
  }, [loadSegmenter, processVideoFrame, loadVirtualBackground]);

  // Stop background processing (but keep raw stream if specified)
  const stopBackgroundProcessing = useCallback((keepRawStream = false) => {
    logger.log('[IVS Panel] Stopping background processing...');
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    if (processingVideoRef.current) {
      processingVideoRef.current.pause();
      processingVideoRef.current.srcObject = null;
      processingVideoRef.current = null;
    }
    
    // IMPORTANT: Stop the canvas/processed stream tracks explicitly
    // This is needed for IVS to properly signal stream removal to other participants
    if (processedStreamRef.current) {
      logger.log('[IVS Panel] Stopping processed stream tracks...');
      processedStreamRef.current.getTracks().forEach(track => {
        logger.log('[IVS Panel] Stopping processed track:', track.kind, track.readyState);
        track.stop();
      });
      processedStreamRef.current = null;
    }
    
    if (!keepRawStream && rawVideoStreamRef.current) {
      logger.log('[IVS Panel] Stopping raw stream tracks...');
      rawVideoStreamRef.current.getTracks().forEach(track => track.stop());
      rawVideoStreamRef.current = null;
    }
    
    canvasRef.current = null;
    offscreenCanvasRef.current = null;
    
    logger.log('[IVS Panel] Background processing stopped');
  }, []);

  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(false);
  const [videoFeedsEnabled, setVideoFeedsEnabled] = useState(true);
  const [localVideoLoading, setLocalVideoLoading] = useState(false);
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [streamUpdateTrigger, setStreamUpdateTrigger] = useState(0); // Force re-render when streams update
  const [participantStreams, setParticipantStreams] = useState<Map<string, MediaStream>>(new Map()); // Track streams by participant ID
  const [activePublishers, setActivePublishers] = useState<Set<string>>(new Set()); // Track which participants are actively publishing video
  const [, setLocalUsername] = useState<string>(userId || 'You');
  const [, setLocalIsIntersectStaff] = useState(isIntersectStaff);
  const [backgroundEffect, setBackgroundEffect] = useState<BackgroundEffect>('none');
  const [isSegmenterLoading, setIsSegmenterLoading] = useState(false);
  const [segmenterError, setSegmenterError] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [previewEffect, setPreviewEffect] = useState<BackgroundEffect>('none');
  
  // Video call specific state - separate IVS stage for video calls (camera/mic)
  const [videoCallToken, setVideoCallToken] = useState<string | null>(null);
  const [videoCallTokenRoom, setVideoCallTokenRoom] = useState<string | undefined>();
  const [videoCallArn, setVideoCallArn] = useState<string | null>(null);
  const [videoCallParticipantId, setVideoCallParticipantId] = useState<string | null>(null);
  const [isVideoCallAvailable, setIsVideoCallAvailable] = useState<boolean>(true); // Assume available until proven otherwise
  const [isFetchingVideoCallToken, setIsFetchingVideoCallToken] = useState<boolean>(false);
  const [isVideoCallConnected, setIsVideoCallConnected] = useState(false); // Video call stage connection status
  const videoCallStageRef = useRef<any>(null); // Separate stage ref for video calls (camera/mic)

  const stageRef = useRef<any>(null); // Legacy ref - keeping for compatibility
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewAnimationRef = useRef<number | null>(null);
  const previewSegmenterRef = useRef<any>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const participantVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const localVideoStreamRef = useRef<LocalStageStream | null>(null);
  const localAudioStreamRef = useRef<LocalStageStream | null>(null);
  const streamsToPublishRef = useRef<any[]>([]); // Ref for video call strategy pattern (camera/mic)
  const visibleFaceUserIdsRef = useRef<Set<string>>(new Set());
  const faceSubscriptionRefreshRef = useRef<number | null>(null);
  const facePinCapacityRef = useRef(getFacePinCapacity());
  const videoFeedsEnabledRef = useRef(true);

  // Background effect refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const segmenterRef = useRef<any>(null);
  const processingVideoRef = useRef<HTMLVideoElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const processedStreamRef = useRef<MediaStream | null>(null);
  const rawVideoStreamRef = useRef<MediaStream | null>(null);
  const virtualBgImageRef = useRef<HTMLImageElement | null>(null);
  const localStreamRef = useRef(localStream); localStreamRef.current = localStream;
  const cameraMounted = useRef(true);

  // Camera ownership is the experience lifetime, not the room/stage lifetime.
  useEffect(() => { cameraMounted.current = true; return () => {
    cameraMounted.current = false;
    stopBackgroundProcessing(false);
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    streamsToPublishRef.current = [];
    localVideoStreamRef.current = null;
    localAudioStreamRef.current = null;
  }; }, [stopBackgroundProcessing]);

  // Keep IVS decode subscriptions aligned with the nearest avatars Unity says are visible.
  // This is the primary memory/CPU guard: off-screen participants are not decoded merely
  // because they share the room.
  useEffect(() => {
    if (!inWorldVideoEnabled) return;

    const handleFaceLayout = (event: Event) => {
      try {
        if (!videoFeedsEnabledRef.current) {
          visibleFaceUserIdsRef.current.clear();
          return;
        }
        const raw = (event as CustomEvent<string | { faces?: FaceLayoutEntry[] }>).detail;
        const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const localIdentity = String(userId ?? '').trim().toLowerCase();
        const layoutFaces = (Array.isArray(payload?.faces) ? payload.faces : [])
          .filter((face: FaceLayoutEntry) => String(face.userId).trim().toLowerCase() !== localIdentity);
        const visibleFaces = selectVisibleFaces(
          layoutFaces,
          facePinCapacityRef.current
        );
        const next = new Set(visibleFaces.map((face) => String(face.userId).trim().toLowerCase()));
        const previous = visibleFaceUserIdsRef.current;
        const changed = next.size !== previous.size || Array.from(next).some((id) => !previous.has(id));
        if (!changed) return;

        visibleFaceUserIdsRef.current = next;
        if (faceSubscriptionRefreshRef.current != null) {
          window.clearTimeout(faceSubscriptionRefreshRef.current);
        }
        faceSubscriptionRefreshRef.current = window.setTimeout(() => {
          faceSubscriptionRefreshRef.current = null;
          const stage = videoCallStageRef.current as any;
          if (stage && typeof stage.refreshStrategy === 'function') {
            try {
              const refreshResult = stage.refreshStrategy();
              if (refreshResult && typeof refreshResult.catch === 'function') {
                void refreshResult.catch((error: unknown) => {
                  console.error('[IVS Face Pins] Failed to refresh visible subscriptions:', error);
                });
              }
            } catch (error) {
              console.error('[IVS Face Pins] Failed to refresh visible subscriptions:', error);
            }
          }
        }, 180);
      } catch {
        visibleFaceUserIdsRef.current = new Set();
      }
    };

    window.addEventListener('VideoFaceLayout', handleFaceLayout as EventListener);
    return () => {
      window.removeEventListener('VideoFaceLayout', handleFaceLayout as EventListener);
      visibleFaceUserIdsRef.current.clear();
      if (faceSubscriptionRefreshRef.current != null) {
        window.clearTimeout(faceSubscriptionRefreshRef.current);
        faceSubscriptionRefreshRef.current = null;
      }
    };
  }, [inWorldVideoEnabled, userId]);

  // Fetch video call token when eventId/roomId are available
  useEffect(() => {
    let cancelled = false;
    const fetchVideoCallToken = async () => {
      if (!accessToken || !eventId || !roomId || isLoading) {
        setVideoCallTokenRoom(undefined);
        logger.log('[IVS Panel] Skipping video call token fetch - missing requirements', {
          hasAccessToken: !!accessToken,
          eventId,
          roomId
        });
        return;
      }

      setIsFetchingVideoCallToken(true);
      
      try {
        logger.log('[IVS Panel] Fetching video call token for room:', roomId);
        const response = await API.getIVSVideoCallTokenForRoom(accessToken, {
          eventId,
          roomId
        });
        if (cancelled) return;

        if (response.status === 200) {
          const data = await response.json();
          if (cancelled) return;
          logger.log('[IVS Panel] ✅ Video call token received:', {
            hasToken: !!data.video_call_token,
            participantId: data.participant_id,
            hasArn: !!data.video_call_arn
          });
          setVideoCallToken(data.video_call_token);
          setVideoCallTokenRoom(roomId);
          setVideoCallArn(data.video_call_arn);
          setVideoCallParticipantId(data.participant_id);
          setIsVideoCallAvailable(true);
        } else if (response.status === 404) {
          logger.log('[IVS Panel] Video calls not enabled for this room');
          setIsVideoCallAvailable(false);
          setVideoCallToken(null);
          setVideoCallArn(null);
          setVideoCallParticipantId(null);
        } else {
          console.error('[IVS Panel] Failed to fetch video call token:', response.status);
          setIsVideoCallAvailable(false);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('[IVS Panel] Error fetching video call token:', error);
        setIsVideoCallAvailable(false);
      } finally {
        if (!cancelled) setIsFetchingVideoCallToken(false);
      }
    };

    fetchVideoCallToken();
    return () => { cancelled = true; };
  }, [accessToken, eventId, roomId, isLoading]);

  // Open preview modal
  const openPreviewModal = useCallback(async () => {
    setShowPreviewModal(true);
    setPreviewEffect(backgroundEffect);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        }, 
        audio: false 
      });
      setPreviewStream(stream);
    } catch (error) {
      console.error('[IVS Panel] Error getting preview stream:', error);
      setSegmenterError('Could not access camera');
    }
  }, [backgroundEffect]);

  // Close preview modal
  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
    
    // Stop preview animation
    if (previewAnimationRef.current) {
      cancelAnimationFrame(previewAnimationRef.current);
      previewAnimationRef.current = null;
    }
    
    // Stop preview stream
    if (previewStream) {
      previewStream.getTracks().forEach(track => track.stop());
      setPreviewStream(null);
    }
  }, [previewStream]);

  // Apply preview settings and start camera
  const applyPreviewAndStart = useCallback(async () => {
    // Save the effect choice
    const effectToUse = previewEffect;
    setBackgroundEffect(effectToUse);
    setLocalVideoLoading(true);
    
    // Stop preview animation first
    if (previewAnimationRef.current) {
      cancelAnimationFrame(previewAnimationRef.current);
      previewAnimationRef.current = null;
    }
    
    // Stop the preview stream
    if (previewStream) {
      previewStream.getTracks().forEach(track => track.stop());
    }
    
    // Close modal UI
    setShowPreviewModal(false);
    setPreviewStream(null);
    
    // Now start the actual camera
    try {
      logger.log('[IVS Panel] 🟢 Starting camera with effect:', effectToUse);
      
      const rawStream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          width: { ideal: 240, max: 320 },
          height: { ideal: 180, max: 240 },
          frameRate: { ideal: 15, max: 20 }
        }, 
        audio: false 
      });
      
      logger.log('[IVS Panel] 🟢 Got raw stream:', rawStream.getVideoTracks().length, 'tracks');
      if (!cameraMounted.current) { rawStream.getTracks().forEach(track => track.stop()); return; }
      rawVideoStreamRef.current = rawStream;
      
      let streamToUse: MediaStream;
      if (effectToUse !== 'none') {
        logger.log('[IVS Panel] 🟢 Starting background processing with effect:', effectToUse);
        const processedStream = await startBackgroundProcessing(rawStream, effectToUse);
        streamToUse = processedStream || rawStream;
        logger.log('[IVS Panel] 🟢 Using processed stream');
      } else {
        streamToUse = rawStream;
        logger.log('[IVS Panel] 🟢 Using raw stream (no effect)');
      }
      
      // Set the stream for display - the useEffect will handle attaching to video element
      if (!cameraMounted.current) { streamToUse.getTracks().forEach(track => track.stop()); return; }
      setLocalStream(streamToUse);
      setLocalVideoEnabled(true);
      
      // Publish to video call stage (video_call_arn)
      const trackToPublish = streamToUse.getVideoTracks()[0];
      if (trackToPublish) {
        localVideoStreamRef.current = new LocalStageStream(trackToPublish);
        streamsToPublishRef.current = [localVideoStreamRef.current];
      }
      if (videoCallStageRef.current && isVideoCallConnected) {
        const stageAny = videoCallStageRef.current as any;
        const videoTrack = streamToUse.getVideoTracks()[0];
        logger.log('[IVS Panel] 🟢 Video track for publish:', videoTrack?.label, videoTrack?.readyState);
        
        if (videoTrack && typeof LocalStageStream !== 'undefined') {
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Published to video_call_arn');
          }
        }
      } else {
        logger.log('[IVS Panel] ⚠️ Video call stage not connected, skipping publish');
      }
      
      setLocalVideoLoading(false);
    } catch (error) {
      console.error('[IVS Panel] ❌ Error starting camera:', error);
      setLocalVideoLoading(false);
    }
  }, [previewEffect, previewStream, startBackgroundProcessing, isVideoCallConnected]);

  // Clear state when room changes
  useEffect(() => {
    // Clear participants and streams when switching rooms
    logger.log('[IVS Panel] Room changed, clearing state');
    setParticipants([]);
    setParticipantStreams(new Map());
    setActivePublishers(new Set());
    setIsConnected(false);
    // Retain the user's active camera and background effect across room changes.
    visibleFaceUserIdsRef.current.clear();
    setShowPreviewModal(false);
    setIsCollapsed(true);
    setPreviewStream((current) => {
      current?.getTracks().forEach((track) => track.stop());
      return null;
    });
    // Reset video call state for new room
    setVideoCallToken(null);
    setVideoCallTokenRoom(undefined);
    setVideoCallArn(null);
    setVideoCallParticipantId(null);
    setIsVideoCallAvailable(true);
  }, [roomId]);

  // Handle preview modal processing
  useEffect(() => {
    if (!showPreviewModal || !previewStream || !previewVideoRef.current) {
      return;
    }

    const video = previewVideoRef.current;
    video.srcObject = previewStream;
    video.play().catch(console.error);

    // If any effect (blur or virtual bg), start processing
    if (previewEffect !== 'none') {
      let animationId: number;
      let segmenter: any = null;
      let bgImage: HTMLImageElement | null = null;

      const startPreviewProcessing = async () => {
        segmenter = await loadSegmenter();
        if (!segmenter || !previewCanvasRef.current) return;
        
        // Load virtual background if needed
        if (previewEffect === 'virtual-neon' || previewEffect === 'intersect-background') {
          bgImage = await new Promise<HTMLImageElement | null>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = previewEffect === 'virtual-neon' ? virtualBgNeon : intersectBg;
          });
        }
        
        previewSegmenterRef.current = segmenter;
        const canvas = previewCanvasRef.current;
        const offCanvas = document.createElement('canvas');
        
        let lastTime = 0;
        const frameInterval = 1000 / 15; // 15 fps for preview
        
        const processFrame = (currentTime: number) => {
          if (!showPreviewModal) return;
          
          if (currentTime - lastTime >= frameInterval) {
            if (video.readyState >= 2 && canvas) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              offCanvas.width = video.videoWidth;
              offCanvas.height = video.videoHeight;
              processVideoFrame(video, canvas, offCanvas, segmenter, previewEffect, bgImage);
            }
            lastTime = currentTime;
          }
          animationId = requestAnimationFrame(processFrame);
        };
        
        animationId = requestAnimationFrame(processFrame);
        previewAnimationRef.current = animationId;
      };

      startPreviewProcessing();

      return () => {
        if (animationId) cancelAnimationFrame(animationId);
      };
    }
  }, [showPreviewModal, previewStream, previewEffect, loadSegmenter, processVideoFrame]);

  // Ensure local video stream is attached to video element when both are ready
  useEffect(() => {
    if (localVideoEnabled && localStream) {
      logger.log('[IVS Panel] 🎥 Stream attachment effect triggered', {
        hasStream: !!localStream,
        trackCount: localStream?.getVideoTracks().length,
        trackState: localStream?.getVideoTracks()[0]?.readyState
      });
      
      // Small delay to ensure video element is mounted
      const attachStream = () => {
        if (localVideoRef.current && localStream) {
          logger.log('[IVS Panel] 🎥 Attaching stream to video element');
          localVideoRef.current.srcObject = localStream;
          localVideoRef.current.play().then(() => {
            logger.log('[IVS Panel] ✅ Video playing');
            setLocalVideoLoading(false);
          }).catch((error) => {
            console.error('[IVS Panel] ❌ Error playing local video:', error);
            setLocalVideoLoading(false);
          });
        } else {
          logger.log('[IVS Panel] ⏳ Video element not ready, retrying...');
          setTimeout(attachStream, 100);
        }
      };
      
      // Start attachment with a small delay
      setTimeout(attachStream, 50);
    }
  }, [localVideoEnabled, localStream]);

  // Initialize video call stage connection
  // Uses video_call_token for publish+subscribe capabilities (camera, mic, video calls)
  useEffect(() => {
    logger.log('[IVS Panel] ==== Initialize video call stage useEffect TRIGGERED ====', {
      hasVideoCallToken: !!videoCallToken,
      hasVideoCallArn: !!videoCallArn,
      isVideoCallAvailable,
      isFetchingVideoCallToken,
      userId
    });
    
    // Wait for video call token to be fetched
    if (isFetchingVideoCallToken) {
      logger.log('[IVS Panel] Still fetching video call token, waiting...');
      return;
    }

    // If video calls not available for this room, skip connection
    if (!isVideoCallAvailable || !videoCallToken || videoCallTokenRoom !== roomId || isLoading) {
      logger.log('[IVS Panel] Skipping video call stage connection - video calls not available or no token');
      return;
    }

    logger.log('[IVS Panel] Connecting to video call stage...');
    let disposed = false;
    let ownedStage: any = null;
    const listeners: Array<[string, (...args: any[]) => void]> = [];
    const connectToStage = async () => {
      try {
        // Create stage instance - Stage needs a strategy object with required methods
        // Based on the IVS docs, we need to provide a complete strategy
        const strategy = {
          // CRITICAL: Return false when we have no streams to publish
          // This signals to IVS that we're no longer a publisher, allowing clean removal on other participants
          shouldPublishParticipant: (): boolean => {
            const shouldPublish = !disposed && streamsToPublishRef.current.length > 0;
            logger.log('[IVS Panel] 📡 shouldPublishParticipant called, returning:', shouldPublish, 'streams:', streamsToPublishRef.current.length);
            return shouldPublish;
          },
          stageStreamsToPublish: (): any[] => {
            logger.log('[IVS Panel] 📡 stageStreamsToPublish called, returning:', streamsToPublishRef.current.length, 'streams');
            return disposed ? [] : streamsToPublishRef.current;
          },
          shouldSubscribeToParticipant: (participant: any): SubscribeType => {
            if (disposed || !videoFeedsEnabledRef.current) return SubscribeType.NONE;
            if (!inWorldVideoEnabled) return SubscribeType.AUDIO_VIDEO;
            const visibleIds = visibleFaceUserIdsRef.current;
            const isVisible = participantIdentityKeys(participant).some((id) => visibleIds.has(id));
            return isVisible ? SubscribeType.AUDIO_VIDEO : SubscribeType.NONE;
          },
          // Face bubbles are tiny, so request the lowest available simulcast layer.
          preferredLayerForStream: (_participant: any, stream: any) => {
            if (stream?.streamType !== StreamType.VIDEO) return undefined;
            return typeof stream.getLowestQualityLayer === 'function'
              ? stream.getLowestQualityLayer()
              : undefined;
          },
        } as any; // Use 'as any' to bypass strict type checking
        
        // Use videoCallToken for video call stage (publish+subscribe capabilities)
        const stage = new Stage(videoCallToken, strategy);
        ownedStage = stage;
        stageRef.current = stage;
        videoCallStageRef.current = stage;

        // Set up event listeners - using type assertion to work around TypeScript errors
        const stageAny = stage as any;
        const on = (event: string, handler: (...args: any[]) => void) => {
          const guarded = (...args: any[]) => { if (!disposed) handler(...args); };
          listeners.push([event, guarded]);
          stageAny.on(event, guarded);
        };
        
        // Try multiple possible event names
        const connectionStateHandler = (state: any) => {
          if (disposed) return;
          const stateStr = typeof state === 'string' ? state : state?.state || state?.connectionState || String(state);
          if (stateStr === 'connected' || stateStr === 'CONNECTED') {
            setIsVideoCallConnected(true);
            setIsConnected(true); // Keep legacy state for UI compatibility
            
            // Extract local participant info when connected
            if (stageAny.localParticipant) {
              const localParticipant = stageAny.localParticipant;
              const localParticipantUserId = localParticipant.userId || 
                                           localParticipant.info?.userId || 
                                           userId || 
                                           'You';
              const localParticipantUsername = localParticipant.attributes?.username || 
                                              localParticipant.info?.attributes?.username ||
                                              localParticipantUserId;
              const intersectStaffStr = localParticipant.attributes?.intersectStaff || 
                                       localParticipant.info?.attributes?.intersectStaff || 
                                       'false';
              const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
              
              setLocalUsername(localParticipantUsername);
              setLocalIsIntersectStaff(isIntersectStaff);
            }
          } else if (stateStr === 'disconnected' || stateStr === 'DISCONNECTED') {
            setIsVideoCallConnected(false);
            setIsConnected(false);
          } else if (stateStr === 'failed' || stateStr === 'FAILED') {
            console.error('❌ Failed to connect to IVS video call stage');
            setIsVideoCallConnected(false);
            setIsConnected(false);
          }
        };

        // Try different event name variations
        on('connection-state-changed', connectionStateHandler);
        on('connectionStateChanged', connectionStateHandler);
        on('state-changed', connectionStateHandler);
        
        // Also use the onConnectionStateChanged method if it exists (saw it in stageKeys)
        if (typeof stageAny.onConnectionStateChanged === 'function') {
          stageAny.onConnectionStateChanged((state: any) => {
            connectionStateHandler(state);
          });
        }
        
        // Also listen for error events
        on('error', (error: any) => {
          console.error('Stage error event:', error);
          setIsConnected(false);
        });

        // Listen for STAGE_PARTICIPANT_STREAMS_ADDED - this is the key event for getting streams!
        on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant: any, streams: any[]) => {
          const participantId = participant.id || participant.info?.id;
          logger.log('[IVS Panel] 📹 STAGE_PARTICIPANT_STREAMS_ADDED EVENT FIRED', { 
            participantId, 
            userId: participant.userId, 
            isLocal: participant.isLocal,
            streamCount: streams.length,
            timestamp: new Date().toISOString()
          });
          
          // For local participant, we already have the stream from getUserMedia
          // Don't override it with the published stream to avoid black screen
          if (participant.isLocal) {
            logger.log('[IVS Panel] 📹 Skipping STREAMS_ADDED for local participant');
            // Just ensure the local video element has the stream
            if (localVideoRef.current && localStream) {
              if (!localVideoRef.current.srcObject) {
                localVideoRef.current.srcObject = localStream;
                localVideoRef.current.play().catch((error) => {
                  console.error('Error playing local video:', error);
                });
              }
            }
            setStreamUpdateTrigger(prev => prev + 1);
            return; // Don't process local participant streams further
          }
          
          // Track that this participant is now publishing
          setActivePublishers((prev) => {
            const newSet = new Set(prev);
            newSet.add(participantId);
            logger.log('[IVS Panel] 🟢 ADDING to activePublishers:', {
              participantId,
              beforeSize: prev.size,
              afterSize: newSet.size,
              allPublishers: Array.from(newSet)
            });
            return newSet;
          });
          
          // Ensure participant is in state (for remote participants)
          setParticipants((prev) => {
            const exists = prev.find((p) => p.id === participantId);
            if (!exists) {
              // Check if participant is Intersect staff
              const intersectStaffStr = participant.attributes?.intersectStaff || 
                                       participant.info?.attributes?.intersectStaff || 
                                       'false';
              const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
              const intersectId = participant.attributes?.intersectId || participant.info?.attributes?.intersectId || '';
              
              return [...prev, {
                id: participantId,
                userId: participant.userId || participant.info?.userId || '',
                identityKeys: participantIdentityKeys(participant),
                publishedTracks: [],
                subscribedTracks: [],
                isIntersectStaff,
                intersectId: intersectId || undefined
              }];
            }
            return prev;
          });
          
          // For remote participants, create MediaStream from tracks
          // Only add VIDEO tracks - audio comes from Unity, not IVS
          const mediaStream = new MediaStream();
          streams.forEach((stream: any) => {
            if (stream.mediaStreamTrack && stream.mediaStreamTrack.kind === 'video') {
              logger.log('[IVS Panel] Adding video track:', stream.mediaStreamTrack.id);
              mediaStream.addTrack(stream.mediaStreamTrack);
            } else if (stream.mediaStreamTrack) {
              logger.log('[IVS Panel] Skipping audio track (audio from Unity):', stream.mediaStreamTrack.id);
            }
          });
          
          if (mediaStream.getTracks().length > 0) {
            logger.log('[IVS Panel] Created MediaStream with', mediaStream.getTracks().length, 'tracks for participant', participantId);
            
            // Store the stream in state so we can attach it when video element is created
            setParticipantStreams((prev) => {
              const newMap = new Map(prev);
              newMap.set(participantId, mediaStream);
              logger.log('[IVS Panel] 🟢 ADDING to participantStreams:', {
                participantId,
                beforeSize: prev.size,
                afterSize: newMap.size,
                allStreams: Array.from(newMap.keys())
              });
              return newMap;
            });
            
            // Also try to attach to existing video element if it exists
            const videoElement = participantVideoRefs.current.get(participantId);
            if (videoElement) {
              logger.log('[IVS Panel] Attaching stream to existing video element for', participantId);
              videoElement.muted = true; // Ensure muted for autoplay
              videoElement.srcObject = mediaStream;
              videoElement.play().catch((error) => {
                console.error('[IVS Panel] Error playing video:', error);
              });
            } else {
              logger.log('[IVS Panel] No video element found yet for', participantId, '- will attach when element is created');
            }
            
            setStreamUpdateTrigger(prev => prev + 1); // Force re-render
          } else {
            logger.warn('[IVS Panel] No tracks found in streams for participant', participantId);
          }
        });
        
        // Listen for streams removed
        on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (participant: any, streams: any[]) => {
          const participantId = participant.id || participant.info?.id;
          logger.log('[IVS Panel] 🚫 STAGE_PARTICIPANT_STREAMS_REMOVED EVENT FIRED', { 
            participantId, 
            userId: participant.userId,
            streamCount: streams.length,
            isLocal: participant.isLocal,
            timestamp: new Date().toISOString()
          });
          
          // CRITICAL: Remove from active publishers
          setActivePublishers((prev) => {
            const hadParticipant = prev.has(participantId);
            const newSet = new Set(prev);
            const deleteResult = newSet.delete(participantId);
            logger.log('[IVS Panel] 🔴 ACTIVELY REMOVING from activePublishers:', { 
              participantId, 
              wasInSet: hadParticipant,
              deleteSuccessful: deleteResult,
              beforeSize: prev.size,
              afterSize: newSet.size,
              remainingPublishers: Array.from(newSet)
            });
            return newSet;
          });
          
          // CRITICAL: Remove stream from state
          setParticipantStreams((prev) => {
            const hadStream = prev.has(participantId);
            const newMap = new Map(prev);
            const deleteResult = newMap.delete(participantId);
            logger.log('[IVS Panel] 🔴 ACTIVELY REMOVING from participantStreams:', {
              participantId,
              wasInMap: hadStream,
              deleteSuccessful: deleteResult,
              beforeSize: prev.size,
              afterSize: newMap.size,
              remainingStreams: Array.from(newMap.keys())
            });
            return newMap;
          });
          
          // CRITICAL: Clean up video element
          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement) {
            logger.log('[IVS Panel] 🔴 Cleaning up video element for:', participantId);
            // Pause video to prevent play errors
            videoElement.pause();
            
            // Clear srcObject immediately when streams are removed
                videoElement.srcObject = null;
            
            // Remove the video element reference completely
            participantVideoRefs.current.delete(participantId);
            logger.log('[IVS Panel] 🔴 Video element removed from refs');
          } else {
            logger.log('[IVS Panel] ⚠️ No video element found in refs for:', participantId);
          }
          
          logger.log('[IVS Panel] 🔴 Triggering UI update after STREAMS_REMOVED');
          setStreamUpdateTrigger(prev => prev + 1);
        });
        
        on(StageEvents.STAGE_PARTICIPANT_JOINED, (participant: any) => {
          setParticipants((prev) => {
            const exists = prev.find((p) => p.id === participant.id);
            if (!exists) {
              // Check if participant is Intersect staff
              const intersectStaffStr = participant.attributes?.intersectStaff || 
                                       participant.info?.attributes?.intersectStaff || 
                                       'false';
              const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
              const intersectId = participant.attributes?.intersectId || participant.info?.attributes?.intersectId || '';
              
              return [...prev, {
                id: participant.id,
                userId: participant.userId || participant.info?.userId || '',
                identityKeys: participantIdentityKeys(participant),
                publishedTracks: [],
                subscribedTracks: [],
                isIntersectStaff,
                intersectId: intersectId || undefined
              }];
            }
            return prev;
          });

          // Check if participant already has published streams - try multiple property names
          const streamsToSubscribe: any[] = [];
          
          if (participant.publishedStreams && participant.publishedStreams.length > 0) {
            streamsToSubscribe.push(...participant.publishedStreams);
          }
          if (participant.streams && participant.streams.length > 0) {
            streamsToSubscribe.push(...participant.streams);
          }
          if (participant.videoStreams && participant.videoStreams.length > 0) {
            streamsToSubscribe.push(...participant.videoStreams);
          }
          if (participant.publishedTracks && participant.publishedTracks.length > 0) {
            streamsToSubscribe.push(...participant.publishedTracks);
          }
          
          const participantIsVisible = !inWorldVideoEnabled || participantIdentityKeys(participant)
            .some((id) => visibleFaceUserIdsRef.current.has(id));
          streamsToSubscribe.forEach((stream: any) => {
            if (!participantIsVisible) return;
            // Subscribe to the stream
            if (stageAny.localParticipant && typeof stageAny.localParticipant.subscribe === 'function') {
              try {
                stageAny.localParticipant.subscribe(stream);
              } catch (e) {
                console.error('Error subscribing via localParticipant:', e);
              }
            } else if (typeof stageAny.subscribe === 'function') {
              try {
                stageAny.subscribe(stream);
              } catch (e) {
                console.error('Error subscribing via stage:', e);
              }
            }
          });

          // Listen for new streams from this participant
          if (typeof participant.on === 'function') {
            participant.on('stream-published', (stream: any) => {
              const isVisible = !inWorldVideoEnabled || participantIdentityKeys(participant)
                .some((id) => visibleFaceUserIdsRef.current.has(id));
              if (!isVisible) return;
              // Subscribe to the stream
              if (stageAny.localParticipant && typeof stageAny.localParticipant.subscribe === 'function') {
                stageAny.localParticipant.subscribe(stream);
              } else if (typeof stageAny.subscribe === 'function') {
                stageAny.subscribe(stream);
              }
            });
          }
        });

        on('participant-left', (participant: any) => {
          const participantId = participant.id || participant.info?.id;
          logger.log('[IVS Panel] 👋 Participant left:', participantId);
          
          // Remove from participants state
          setParticipants((prev) => prev.filter((p) => p.id !== participantId));
          
          // Remove from active publishers
          setActivePublishers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(participantId);
            logger.log('[IVS Panel] Active publishers after removal:', newSet.size);
            return newSet;
          });
          
          // Remove from participant streams
          setParticipantStreams((prev) => {
            const newMap = new Map(prev);
            newMap.delete(participantId);
            logger.log('[IVS Panel] Participant streams after removal:', newMap.size);
            return newMap;
          });
          
          // Clean up video element
          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement) {
            logger.log('[IVS Panel] Cleaning up video element for:', participantId);
            // Pause first to prevent play errors
            videoElement.pause();
            videoElement.srcObject = null;
            participantVideoRefs.current.delete(participantId);
          }
          
          // Force UI update
          setStreamUpdateTrigger(prev => prev + 1);
        });

        // Try multiple event names for stream subscription
        const handleStreamSubscribed = (data: any) => {
          // Handle different data formats
          let participantId: string | undefined;
          let stream: any;
          
          if (typeof data === 'object' && data !== null) {
            participantId = data.participantId || data.participant?.id || data.participantId || data.participantId;
            stream = data.stream || data.remoteStream || data;
          } else {
            stream = data;
          }
          
          // If we have a stream but no participantId, try to get it from the stream
          if (stream && !participantId) {
            participantId = stream.participantId || stream.participant?.id;
          }
          
          if (!participantId) {
            // Try to find participant by matching stream
            let allParticipants: any[] = [];
            if (stageAny.participants instanceof Map) {
              allParticipants = Array.from(stageAny.participants.values());
            } else if (Array.isArray(stageAny.participants)) {
              allParticipants = stageAny.participants;
            }
            
            for (const p of allParticipants) {
              if (p.publishedStreams?.includes(stream) || p.streams?.includes(stream)) {
                participantId = p.id;
                break;
              }
            }
          }

          if (!participantId) {
            return;
          }

          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement && stream) {
            try {
              // Try to get MediaStream from the stream object
              let mediaStream: MediaStream | null = null;
              if (typeof stream.getMediaStream === 'function') {
                mediaStream = stream.getMediaStream();
              } else if (stream.mediaStream) {
                mediaStream = stream.mediaStream;
              } else if (stream instanceof MediaStream) {
                mediaStream = stream;
              } else if (stream.stream && stream.stream instanceof MediaStream) {
                mediaStream = stream.stream;
              }
              
              if (mediaStream) {
                // Filter to only video tracks - audio comes from Unity, not IVS
                const videoOnlyStream = new MediaStream();
                mediaStream.getVideoTracks().forEach(track => videoOnlyStream.addTrack(track));
                
                videoElement.muted = true; // Ensure muted for autoplay
                videoElement.srcObject = videoOnlyStream;
                videoElement.play().catch((error) => {
                  console.error('Error playing video:', error);
                });
                // Force re-render to update UI
                setStreamUpdateTrigger(prev => prev + 1);
              }
            } catch (error) {
              console.error('Error setting up video element:', error);
            }
          }
        };
        
        on('stream-subscribed', handleStreamSubscribed);
        on('streamSubscribed', handleStreamSubscribed);
        on('remote-stream-subscribed', handleStreamSubscribed);

        on('stream-unsubscribed', (data: any) => {
          const participantId = data.participantId || data.id;
          logger.log('[IVS Panel] 📴 Stream unsubscribed for:', participantId);
          
          // Remove from active publishers
          setActivePublishers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(participantId);
            return newSet;
          });
          
          // Remove from participant streams
          setParticipantStreams((prev) => {
            const newMap = new Map(prev);
            newMap.delete(participantId);
            return newMap;
          });
          
          // Clean up video element completely
          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement) {
            videoElement.pause();
            videoElement.srcObject = null;
            participantVideoRefs.current.delete(participantId);
          }
          
          // Force UI update
          setStreamUpdateTrigger(prev => prev + 1);
        });

        // Join the video call stage - per IVS docs, use stage.join()
        try {
          if (typeof stageAny.join === 'function') {
            logger.log('[IVS Panel] 🟢 Calling stage.join() for video call stage...');
            await stageAny.join();
            if (disposed) { stageAny.leave(); return; }
            logger.log('[IVS Panel] ✅ Joined video call stage successfully');
          } else {
            console.error('[IVS Panel] ❌ stage.join() not available!');
          }
        } catch (error) {
          console.error('[IVS Panel] ❌ Error joining video call stage:', error);
          throw error;
        }

        // Check initial connection state - it might be a property, not a method
        let initialState: any;
        if (typeof stageAny.getConnectionState === 'function') {
          initialState = stageAny.getConnectionState();
        } else if (stageAny.connectionState !== undefined) {
          initialState = stageAny.connectionState;
        }
        logger.log('[IVS Panel] 🔍 Initial video call stage connection state:', initialState);
        if (initialState === 'connected' || initialState === 'CONNECTED') {
          logger.log('[IVS Panel] ✅ Video call stage already connected!');
          setIsVideoCallConnected(true);
          setIsConnected(true);
        }

        // Extract local participant info (username and intersectStaff)
        if (stageAny.localParticipant) {
          const localParticipant = stageAny.localParticipant;
          const localParticipantUserId = localParticipant.userId || 
                                       localParticipant.info?.userId || 
                                       userId || 
                                       'You';
          const localParticipantUsername = localParticipant.attributes?.username || 
                                          localParticipant.info?.attributes?.username ||
                                          localParticipantUserId;
          const intersectStaffStr = localParticipant.attributes?.intersectStaff || 
                                   localParticipant.info?.attributes?.intersectStaff || 
                                   'false';
          const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
          
          setLocalUsername(localParticipantUsername);
          setLocalIsIntersectStaff(isIntersectStaff);
        }

        // Function to check and subscribe to participants
        const checkAndSubscribeToParticipants = () => {
          if (disposed) return;
          // logger.log('[IVS Panel] 🔍 Checking for existing participants...');
          
          // Try different ways to access participants
          let existingParticipants: any[] = [];
          
          // Try multiple ways to get participants
          if (stageAny.participants) {
            // Check if it's a Map
            if (stageAny.participants instanceof Map) {
              existingParticipants = Array.from(stageAny.participants.values());
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from Map');
            } else if (Array.isArray(stageAny.participants)) {
              existingParticipants = stageAny.participants;
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from Array');
            }
          }
          
          if (existingParticipants.length === 0) {
            // Try other methods as fallback
            if (stageAny.getParticipants && typeof stageAny.getParticipants === 'function') {
              existingParticipants = stageAny.getParticipants();
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from getParticipants()');
            } else if (stageAny.remoteParticipants && Array.isArray(stageAny.remoteParticipants)) {
              existingParticipants = stageAny.remoteParticipants;
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from remoteParticipants');
            } else if (stageAny.getAllParticipants && typeof stageAny.getAllParticipants === 'function') {
              existingParticipants = stageAny.getAllParticipants();
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from getAllParticipants()');
            }
          }
          
          const localParticipantId = stageAny.localParticipant?.id || stageAny.localParticipant?.info?.id;
          // logger.log('[IVS Panel] Local participant ID:', localParticipantId);
          
          existingParticipants.forEach((participant: any) => {
            // Try different ways to get participant ID
            const participantId = participant.id || participant.participantId || participant.info?.id || participant.userId;
            
            // Skip local participant
            if (participantId === localParticipantId) {
              return;
            }
            
            // Try to find streams - RemoteStageParticipant might have different properties
            const streamsToSubscribe: any[] = [];
            
            // logger.log('[IVS Panel] Processing participant:', {
            //   id: participantId,
            //   userId: participant.userId,
            //   hasPublishedStreams: !!participant.publishedStreams,
            //   hasStreams: !!participant.streams
            // });
            
            // Check various possible stream properties
            if (participant.publishedStreams && Array.isArray(participant.publishedStreams) && participant.publishedStreams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.publishedStreams.length, 'published streams for', participantId);
              streamsToSubscribe.push(...participant.publishedStreams);
            }
            if (participant.streams && Array.isArray(participant.streams) && participant.streams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.streams.length, 'streams for', participantId);
              streamsToSubscribe.push(...participant.streams);
            }
            if (participant.videoStreams && Array.isArray(participant.videoStreams) && participant.videoStreams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.videoStreams.length, 'video streams for', participantId);
              streamsToSubscribe.push(...participant.videoStreams);
            }
            if (participant.subscribedStreams && Array.isArray(participant.subscribedStreams) && participant.subscribedStreams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.subscribedStreams.length, 'subscribed streams for', participantId);
              streamsToSubscribe.push(...participant.subscribedStreams);
            }
            
            // Check if participant has a method to get streams
            if (typeof participant.getPublishedStreams === 'function') {
              try {
                const published = participant.getPublishedStreams();
                if (Array.isArray(published)) {
                  // logger.log('[IVS Panel] Got', published.length, 'streams via getPublishedStreams() for', participantId);
                  streamsToSubscribe.push(...published);
                }
              } catch (e) {
                console.error('[IVS Panel] Error calling getPublishedStreams:', e);
              }
            }
            
            // logger.log('[IVS Panel] Total streams to process for', participantId, ':', streamsToSubscribe.length);
            
            setParticipants((prev) => {
              const participantId = participant.id || (participant as any).participantId || (participant as any).info?.id || (participant as any).userId;
              const exists = prev.find((p) => {
                const pId = p.id || (p as any).participantId || (p as any).info?.id || (p as any).userId;
                return pId === participantId;
              });
              if (!exists) {
                // Check if participant is Intersect staff
                const intersectStaffStr = participant.attributes?.intersectStaff || 
                                         participant.info?.attributes?.intersectStaff || 
                                         'false';
                const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
                const intersectId = participant.attributes?.intersectId || participant.info?.attributes?.intersectId || '';
                
                // Process existing streams - create MediaStream from tracks (same as STAGE_PARTICIPANT_STREAMS_ADDED)
                // Only add VIDEO tracks - audio comes from Unity, not IVS
                if (streamsToSubscribe.length > 0) {
                  // logger.log('[IVS Panel] Creating MediaStream from', streamsToSubscribe.length, 'streams for existing participant', participantId);
                  
                  const mediaStream = new MediaStream();
                  let trackCount = 0;
                  
                  streamsToSubscribe.forEach((stream: any) => {
                    if (stream.mediaStreamTrack && stream.mediaStreamTrack.kind === 'video') {
                      // logger.log('[IVS Panel] Adding video track for', participantId);
                      mediaStream.addTrack(stream.mediaStreamTrack);
                      trackCount++;
                    }
                  });
                  
                  if (trackCount > 0) {
                    // logger.log('[IVS Panel] ✅ Created MediaStream with', trackCount, 'tracks for existing participant', participantId);
                    
                    // Track that this participant is publishing
                    setActivePublishers((prev) => {
                      const newSet = new Set(prev);
                      newSet.add(participantId);
                      return newSet;
                    });
                    
                    // Store the stream in state
                    setParticipantStreams((prev) => {
                      const newMap = new Map(prev);
                      newMap.set(participantId, mediaStream);
                      return newMap;
                    });
                    
                    // Force re-render to show the stream
                        setStreamUpdateTrigger(prev => prev + 1);
                  } else {
                    // logger.warn('[IVS Panel] ⚠️ No tracks found in streams for existing participant', participantId);
                  }
                }
                
                return [...prev, {
                  id: participantId,
                  userId: participant.userId || participant.info?.userId || participantId,
                  identityKeys: participantIdentityKeys(participant),
                  publishedTracks: [],
                  subscribedTracks: [],
                  isIntersectStaff,
                  intersectId: intersectId || undefined
                }];
              }
              return prev;
            });
          });
        };
        
        // Check immediately
        checkAndSubscribeToParticipants();
        
        // Also check after a delay to catch participants that join after connection
        setTimeout(() => {
          checkAndSubscribeToParticipants();
        }, 2000);
        
        setTimeout(() => {
          checkAndSubscribeToParticipants();
        }, 5000);

        // Also check connectionState property periodically for video call stage
        const checkConnectionState = () => {
          const currentState = stageAny.connectionState || (stageAny.getConnectionState && stageAny.getConnectionState());
          if (currentState === 'connected' || currentState === 'CONNECTED') {
            if (!isVideoCallConnected) {
              logger.log('[IVS Panel] ✅ Video call stage connection detected via polling');
              setIsVideoCallConnected(true);
              setIsConnected(true);
            }
          }
        };
        
        // Poll connection state every second for first 15 seconds
        const stateCheckInterval = setInterval(checkConnectionState, 1000);
        setTimeout(() => clearInterval(stateCheckInterval), 15000);

          // Periodically check for new participant streams to subscribe to
        const checkForStreams = () => {
          if (disposed) return;
          if (!isVideoCallConnected) return;
          
          // Try multiple ways to get participants
          let allParticipants: any[] = [];
          if (stageAny.participants) {
            // Check if it's a Map
            if (stageAny.participants instanceof Map) {
              allParticipants = Array.from(stageAny.participants.values());
            } else if (Array.isArray(stageAny.participants)) {
              allParticipants = stageAny.participants;
            }
          }
          
          if (allParticipants.length === 0) {
            // Try other methods as fallback
            if (stageAny.getParticipants && typeof stageAny.getParticipants === 'function') {
              allParticipants = stageAny.getParticipants();
            } else if (stageAny.remoteParticipants && Array.isArray(stageAny.remoteParticipants)) {
              allParticipants = stageAny.remoteParticipants;
            }
          }
          
          // Also re-run the full participant check
          checkAndSubscribeToParticipants();
          
          allParticipants.forEach((participant: any) => {
            if (!participant || participant.id === stageAny.localParticipant?.id) {
              return; // Skip self
            }
            
            // Check for streams we haven't subscribed to
            const streams = participant.publishedStreams || participant.streams || participant.videoStreams || [];
            streams.forEach((stream: any) => {
              const isVisible = !inWorldVideoEnabled || participantIdentityKeys(participant)
                .some((id) => visibleFaceUserIdsRef.current.has(id));
              if (!isVisible) return;
              // Check if we're already subscribed (this is a simple check)
              const videoElement = participantVideoRefs.current.get(participant.id);
              if (videoElement && !videoElement.srcObject) {
                // Try to subscribe
                if (stageAny.localParticipant && typeof stageAny.localParticipant.subscribe === 'function') {
                  try {
                    stageAny.localParticipant.subscribe(stream);
                  } catch (e) {
                    console.error('Error auto-subscribing:', e);
                  }
                }
              }
            });
          });
        };
        
        // Check for streams every 2 seconds
        const streamCheckInterval = setInterval(checkForStreams, 2000);
        setTimeout(() => clearInterval(streamCheckInterval), 60000); // Stop after 60 seconds

        // Set a timeout to detect if connection is stuck
        const connectionTimeout = setTimeout(() => {
          if (!isConnected) {
          }
        }, 10000);

        // Clear timeout if connected
        const originalHandler = connectionStateHandler;
        const wrappedHandler = (state: any) => {
          originalHandler(state);
          const stateStr = typeof state === 'string' ? state : state?.state || state?.connectionState || String(state);
          if (stateStr === 'connected' || stateStr === 'CONNECTED') {
            clearTimeout(connectionTimeout);
          }
        };

        // Re-register with wrapped handler
        const originalListener = listeners.find(([event]) => event === 'connection-state-changed');
        if (originalListener) stageAny.off(...originalListener);
        on('connection-state-changed', wrappedHandler);
      } catch (error) {
        if (disposed) return;
        console.error('❌ Error connecting to IVS video call stage:', error);
        console.error('Error details:', {
          message: (error as Error).message,
          stack: (error as Error).stack,
          name: (error as Error).name
        });
        setIsVideoCallConnected(false);
        setIsConnected(false);
      }
    };

    logger.log('[IVS Panel] 🟢 Starting video call stage connection with token length:', videoCallToken?.length);
    connectToStage();

    // Cleanup
    return () => {
      logger.log('[IVS Panel] Cleaning up stage connection...');
      
      // Leave the old room without stopping the camera. Its strategy is now inert;
      // the next authorized room stage inherits the existing publish tracks.
      disposed = true;
      listeners.forEach(([event, handler]) => ownedStage?.off?.(event, handler));

      // Disconnect from video call stage
      if (ownedStage) {
        const stageAny = ownedStage;
        try {
        if (stageAny.leave) {
          stageAny.leave();
            logger.log('[IVS Panel] Left video call stage');
        } else if (stageAny.disconnect) {
          stageAny.disconnect();
            logger.log('[IVS Panel] Disconnected from video call stage');
          }
        } catch (e) {
          console.error('Error disconnecting video call stage:', e);
        }
        if (videoCallStageRef.current === ownedStage) videoCallStageRef.current = null;
        if (stageRef.current === ownedStage) stageRef.current = null;
      }
      
      // Clear video element refs
      participantVideoRefs.current.clear();
      
      // Clear participant streams and active publishers
      setParticipantStreams(new Map());
      setActivePublishers(new Set());
      setIsVideoCallConnected(false);
      
      logger.log('[IVS Panel] Video call stage cleanup complete');
    };
  }, [videoCallToken, videoCallArn, videoCallTokenRoom, roomId, isLoading, isVideoCallAvailable, isFetchingVideoCallToken, inWorldVideoEnabled]);

  // Effect to attach streams to video elements when streams arrive after elements are created
  // This handles the case where a user joins a room with existing participants
  useEffect(() => {
    logger.log('[IVS Panel] 🔄 Stream attachment effect triggered', {
      streamCount: participantStreams.size,
      videoElementCount: participantVideoRefs.current.size,
      participantsCount: participants.length
    });
    
    // Clean up orphaned streams (streams without participants)
    const orphanedStreams: string[] = [];
    participantStreams.forEach((stream, participantId) => {
      const participantExists = participants.some(p => p.id === participantId);
      if (!participantExists) {
        orphanedStreams.push(participantId);
      }
    });
    
    if (orphanedStreams.length > 0) {
      logger.log('[IVS Panel] 🧹 Cleaning up orphaned streams:', orphanedStreams);
      setParticipantStreams((prev) => {
        const newMap = new Map(prev);
        orphanedStreams.forEach(id => newMap.delete(id));
        return newMap;
      });
      setActivePublishers((prev) => {
        const newSet = new Set(prev);
        orphanedStreams.forEach(id => newSet.delete(id));
        return newSet;
      });
      // Don't continue with attachment if we're cleaning up
      return;
    }
    
    // For each participant with a stream, make sure their video element has it
    participantStreams.forEach((stream, participantId) => {
      const videoElement = participantVideoRefs.current.get(participantId);
      if (videoElement && videoElement.srcObject !== stream) {
        logger.log('[IVS Panel] 📹 Attaching late-arriving stream to existing element:', participantId);
        videoElement.muted = true; // Ensure muted for autoplay
        videoElement.srcObject = stream;
        videoElement.play().catch((error) => {
          console.error('[IVS Panel] Error playing video for', participantId, error);
        });
      } else if (!videoElement) {
        logger.log('[IVS Panel] ⏳ Stream exists but video element not yet created for:', participantId);
      }
    });
  }, [participantStreams, streamUpdateTrigger, participants]);

  // Handle local video/audio toggle - uses video call stage (video_call_arn)
  const toggleLocalVideo = async () => {
    if (!localVideoEnabled && (!videoCallStageRef.current || !isVideoCallConnected)) {
      logger.log('[IVS Panel] Video call stage not connected');
      return;
    }

    try {
      if (localVideoEnabled) {
        logger.log('[IVS Panel] 🔴 TURNING OFF LOCAL VIDEO (video_call_arn)...');
        
        const stageAny = videoCallStageRef.current as any;
        
        // CRITICAL: Capture stream references BEFORE clearing state
        const streamToStop = localStream;
        // Stop intent takes effect immediately, even if another room connects during cleanup.
        streamsToPublishRef.current = [];
        localVideoStreamRef.current = null;
        
        try {
          // Step 1: Stop media tracks FIRST (critical for IVS cleanup)
          if (streamToStop) {
            logger.log('[IVS Panel] 🔴 Step 1: Stopping local stream tracks...');
            streamToStop.getTracks().forEach((track) => {
              logger.log('[IVS Panel] 🔴 Stopping track:', track.kind, track.id, track.readyState);
              track.stop();
            });
          }
          
          // Step 2: Stop background processing (canvas/raw streams)
          logger.log('[IVS Panel] 🔴 Step 2: Stopping background processing...');
          stopBackgroundProcessing(false);
          
          // Step 3: Wait for tracks to fully end before refreshStrategy
          // IVS needs time to detect track ended state for canvas streams
          logger.log('[IVS Panel] 🔴 Step 3: Waiting for tracks to end...');
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Step 4: Clear streams from publish strategy
          logger.log('[IVS Panel] 🔴 Step 4: Clearing streams from publish strategy...');
          streamsToPublishRef.current = [];
          localVideoStreamRef.current = null;
          
          // Step 5: Call refreshStrategy to unpublish from IVS
          if (stageAny && typeof stageAny.refreshStrategy === 'function') {
            logger.log('[IVS Panel] 🔴 Step 5: Calling refreshStrategy to unpublish...');
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ refreshStrategy completed');
          }
          
          // Step 6: Safety check - remove local participant from tracking if somehow added
          const localParticipantId = stageAny?.localParticipant?.id || stageAny?.localParticipant?.info?.id;
          if (localParticipantId) {
            logger.log('[IVS Panel] 🔴 Step 6: Safety check - Removing local participant from tracking:', localParticipantId);
            setActivePublishers((prev) => {
              if (prev.has(localParticipantId)) {
                const newSet = new Set(prev);
                newSet.delete(localParticipantId);
                logger.log('[IVS Panel] 🔴 Removed local participant from activePublishers');
                return newSet;
              }
              logger.log('[IVS Panel] 🔴 Local participant was not in activePublishers');
              return prev;
            });
            setParticipantStreams((prev) => {
              if (prev.has(localParticipantId)) {
                const newMap = new Map(prev);
                newMap.delete(localParticipantId);
                logger.log('[IVS Panel] 🔴 Removed local participant from participantStreams');
                return newMap;
              }
              logger.log('[IVS Panel] 🔴 Local participant was not in participantStreams');
              return prev;
            });
          } else {
            logger.log('[IVS Panel] ⚠️ Could not get local participant ID for cleanup');
          }
        } catch (error) {
          console.error('[IVS Panel] ❌ Error during unpublish:', error);
          stopBackgroundProcessing(false);
        }
        
        // Step 7: Clear the local video element UI
        if (localVideoRef.current) {
          logger.log('[IVS Panel] 🔴 Step 7: Clearing local video element');
          localVideoRef.current.pause();
          localVideoRef.current.srcObject = null;
        }
        
        // Step 8: Update UI state LAST
        setLocalVideoEnabled(false);
        setLocalStream(null);
        setLocalVideoLoading(false);
        
        logger.log('[IVS Panel] ✅ LOCAL VIDEO COMPLETELY TURNED OFF');
        logger.log('[IVS Panel] ✅ Other participants should see clean removal (no frozen frame)');
      } else {
        logger.log('[IVS Panel] 🟢 TURNING ON LOCAL VIDEO - Starting publish process...');
        
        // Start loading state
        setLocalVideoLoading(true);
        
        // Start video with efficient constraints (480p for better performance)
        const rawStream = await navigator.mediaDevices.getUserMedia({ 
          video: {
            width: { ideal: 240 },
            height: { ideal: 135 },
            frameRate: { ideal: 15, max: 20 }
          }, 
          audio: false 
        });
        
        logger.log('[IVS Panel] 🟢 Got local video stream with', rawStream.getVideoTracks().length, 'tracks');
        
        // Store raw stream reference
        rawVideoStreamRef.current = rawStream;
        
        // Apply background effect if enabled
        let stream: MediaStream;
        if (backgroundEffect !== 'none') {
          logger.log('[IVS Panel] 🟢 Applying background effect:', backgroundEffect);
          const processedStream = await startBackgroundProcessing(rawStream, backgroundEffect);
          stream = processedStream || rawStream;
        } else {
          stream = rawStream;
        }
        
        // Set stream and enable video immediately so element renders
        setLocalStream(stream);
        setLocalVideoEnabled(true);
        
        // Use setTimeout to ensure the video element is rendered before attaching stream
        // The useEffect will also handle this, but this ensures it happens quickly
        setTimeout(() => {
          if (localVideoRef.current && stream) {
            localVideoRef.current.srcObject = stream;
            localVideoRef.current.play().then(() => {
              // Video is playing, hide loading indicator
              setLocalVideoLoading(false);
            }).catch((error) => {
              console.error('Error playing local video:', error);
              setLocalVideoLoading(false);
            });
          }
        }, 0);

        // Create LocalStageStream from MediaStream
        // LocalStageStream constructor might need MediaStreamTrack(s) or different parameters
        const stageAny = videoCallStageRef.current as any;
        let localStageStream: any;
        
        try {
          // LocalStageStream likely needs MediaStreamTrack, not MediaStream
          const videoTrack = stream.getVideoTracks()[0];
          if (!videoTrack) {
            throw new Error('No video track found in stream');
          }
          
          if (typeof LocalStageStream !== 'undefined') {
            // Try with video track first (most likely)
            localStageStream = new (LocalStageStream as any)(videoTrack);
          } else {
            throw new Error('LocalStageStream not available');
          }
        } catch (e1) {
          try {
            // Fallback: try with MediaStream
            if (typeof LocalStageStream !== 'undefined') {
              localStageStream = new (LocalStageStream as any)(stream);
            } else {
              throw e1;
            }
          } catch (e2) {
            console.error('Failed to create LocalStageStream:', { e1, e2 });
            throw e2;
          }
        }
        
        // Publish the stream to video call stage (video_call_arn)
        try {
          logger.log('[IVS Panel] 🟢 Adding stream to streamsToPublishRef (video call stage)...');
          // Add stream to the ref
          streamsToPublishRef.current = [localStageStream];
          
          logger.log('[IVS Panel] 🟢 Calling refreshStrategy to publish to video_call_arn...');
          // Refresh strategy to publish the stream
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Successfully published video stream to video_call_arn');
          } else {
            console.error('[IVS Panel] ❌ No refreshStrategy method found');
            throw new Error('refreshStrategy method not available');
          }
        } catch (publishError) {
          console.error('[IVS Panel] ❌ Error publishing stream:', publishError);
          throw publishError;
        }
        
        localVideoStreamRef.current = localStageStream;
        logger.log('[IVS Panel] 🟢 LOCAL VIDEO TURNED ON (video_call_arn)');
        // localVideoEnabled is already set to true above
      }
    } catch (error) {
      console.error('Error toggling video:', error);
    }
  };

  const toggleVideoFeeds = async () => {
    const nextEnabled = !videoFeedsEnabled;
    videoFeedsEnabledRef.current = nextEnabled;
    setVideoFeedsEnabled(nextEnabled);

    if (!nextEnabled) {
      visibleFaceUserIdsRef.current.clear();
      closePreviewModal();
      if (localVideoEnabled) {
        await toggleLocalVideo();
      }
    }

    const stage = videoCallStageRef.current as any;
    if (stage && typeof stage.refreshStrategy === 'function') {
      try {
        const refreshResult = stage.refreshStrategy();
        if (refreshResult && typeof refreshResult.catch === 'function') {
          await refreshResult;
        }
      } catch (error) {
        console.error('[IVS Panel] Failed to update video feed subscriptions:', error);
      }
    }

    if (nextEnabled && !localVideoEnabled) {
      await openPreviewModal();
    }
  };

  // Toggle local audio - uses video call stage (video_call_arn)
  const toggleLocalAudio = async () => {
    if (!videoCallStageRef.current || !isVideoCallConnected) {
      logger.log('[IVS Panel] Video call stage not connected');
      return;
    }

    try {
      if (localAudioEnabled) {
        // Stop audio - unpublish using strategy pattern
        if (localAudioStreamRef.current) {
          const stageAny = videoCallStageRef.current as any;
          try {
            // Remove stream from ref
            streamsToPublishRef.current = streamsToPublishRef.current.filter(
              s => s !== localAudioStreamRef.current
            );
            
            // Refresh strategy to unpublish
            if (typeof stageAny.refreshStrategy === 'function') {
              await stageAny.refreshStrategy();
              logger.log('[IVS Panel] Successfully unpublished audio from video_call_arn');
            }
          } catch (error) {
            console.error('Error unpublishing audio:', error);
          }
          localAudioStreamRef.current = null;
        }
        if (localStream) {
          localStream.getAudioTracks().forEach((track) => track.stop());
        }
        setLocalAudioEnabled(false);
      } else {
        // Start audio
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: false, 
          audio: true 
        });
        
        // Create LocalStageStream from MediaStream for audio
        const stageAny = videoCallStageRef.current as any;
        let localStageStream: any;
        
        try {
          // LocalStageStream likely needs MediaStreamTrack, not MediaStream
          const audioTrack = stream.getAudioTracks()[0];
          if (!audioTrack) {
            throw new Error('No audio track found in stream');
          }
          
          if (typeof LocalStageStream !== 'undefined') {
            // Try with audio track first (most likely)
            localStageStream = new (LocalStageStream as any)(audioTrack);
          } else {
            throw new Error('LocalStageStream not available');
          }
        } catch (e1) {
          try {
            // Fallback: try with MediaStream
            if (typeof LocalStageStream !== 'undefined') {
              localStageStream = new (LocalStageStream as any)(stream);
            } else {
              throw e1;
            }
          } catch (e2) {
            console.error('Failed to create LocalStageStream for audio:', { e1, e2 });
            throw e2;
          }
        }
        
        // Publish to video call stage (video_call_arn)
        try {
          // Add stream to the ref
          streamsToPublishRef.current = [...streamsToPublishRef.current, localStageStream];
          
          // Refresh strategy to publish the stream
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] Successfully published audio to video_call_arn');
          } else {
            console.error('No refreshStrategy method found');
            throw new Error('refreshStrategy method not available');
          }
        } catch (publishError) {
          console.error('Error publishing audio stream:', publishError);
          throw publishError;
        }
        
        localAudioStreamRef.current = localStageStream;
        setLocalAudioEnabled(true);
      }
    } catch (error) {
      console.error('Error toggling audio:', error);
    }
  };

  // Toggle background effect while camera is running
  const toggleBackgroundEffect = async (newEffect: BackgroundEffect) => {
    if (!isVideoCallConnected) return;
    
    setBackgroundEffect(newEffect);
    
    // If camera is on, we need to reprocess the stream
    if (localVideoEnabled && rawVideoStreamRef.current) {
      logger.log('[IVS Panel] 🔄 Changing background effect to:', newEffect);
      
      // Stop current processing
      stopBackgroundProcessing();
      
      // Get fresh raw stream or use existing
      let rawStream = rawVideoStreamRef.current;
      
      // If tracks are ended, get new ones
      if (rawStream.getVideoTracks().every(t => t.readyState === 'ended')) {
        rawStream = await navigator.mediaDevices.getUserMedia({ 
          video: {
            width: { ideal: 240 },
            height: { ideal: 135 },
            frameRate: { ideal: 15, max: 20 }
          }, 
          audio: false 
        });
        rawVideoStreamRef.current = rawStream;
      }
      
      let newStream: MediaStream;
      if (newEffect !== 'none') {
        const processedStream = await startBackgroundProcessing(rawStream, newEffect);
        newStream = processedStream || rawStream;
      } else {
        newStream = rawStream;
      }
      
      // Update local stream display
      setLocalStream(newStream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = newStream;
        localVideoRef.current.play().catch(console.error);
      }
      
      // Update published stream on video call stage
      const stageAny = videoCallStageRef.current as any;
      if (stageAny && typeof stageAny.refreshStrategy === 'function') {
        // Create new LocalStageStream
        const videoTrack = newStream.getVideoTracks()[0];
        if (videoTrack && typeof LocalStageStream !== 'undefined') {
          const localStageStream = new (LocalStageStream as any)(videoTrack);
          streamsToPublishRef.current = [localStageStream];
          localVideoStreamRef.current = localStageStream;
          await stageAny.refreshStrategy();
          logger.log('[IVS Panel] ✅ Published stream updated with new effect on video_call_arn');
        }
      }
    }
  };

  // Note: Subscription is handled via event listeners in the connection effect

  // Filter participants to only show those with active video streams
  // Check both the stored streams map and video element refs
  // In debug mode, show all debug participants regardless of streams
  // Limit to max 12 total video publishers (including local user if they're publishing)
  const MAX_VIDEO_PARTICIPANTS = 12;
  
  // Count actual remote publishers
  // Use activePublishers as source of truth (not video element srcObject)
  const remotePublishersCount = debugMode 
    ? activePublishers.size + debugParticipants.length
    : activePublishers.size;
  
  // Total publishers = remote publishers + local user (if publishing)
  const totalPublishers = remotePublishersCount + (localVideoEnabled ? 1 : 0);
  const isAtVideoLimit = totalPublishers >= MAX_VIDEO_PARTICIPANTS;
  
  logger.log('[IVS Panel] Publisher counts:', {
    remotePublishers: remotePublishersCount,
    localPublishing: localVideoEnabled,
    totalPublishers,
    isAtVideoLimit,
    maxAllowed: MAX_VIDEO_PARTICIPANTS,
    activePublishersArray: Array.from(activePublishers),
    participantStreamsKeys: Array.from(participantStreams.keys())
  });
  
  // Get participants with streams for rendering (up to limit)
  // IMPORTANT: Only check state (participantStreams and activePublishers), not video element srcObject
  // This prevents showing stale/frozen video when a user turns off their camera
  const participantsWithStreams = debugMode 
    ? [...participants.filter((participant) => {
        // Check if they're actively publishing (source of truth)
        return activePublishers.has(participant.id);
      }), ...debugParticipants]
    : participants.filter((participant) => {
        // Check if they're actively publishing (source of truth)
        return activePublishers.has(participant.id);
      });

  const disableUnityCapture = () => {
    if (window.unityInstance) {
      try {
        window.unityInstance.SendMessage('BrowserBridge', 'DisableKeyboard', '');
        window.unityInstance.SendMessage('BrowserBridge', 'DisableCursorCapture', '');
        logger.log('[Video Panel] Unity capture disabled');
      } catch (error) {
        console.error('[Video Panel] Error disabling Unity capture:', error);
      }
    }
  };

  // Retained temporarily as a non-rendered reference while the camera/session logic is
  // migrated away from the former sidebar. The live UI returned below is dock-native.
  const renderLegacyPanel = () => (
    <>
    <div 
      className={`unity-ivs-glass-sidebar absolute top-0 right-0 h-full flex flex-col transition-[width,min-width,max-width,box-shadow] duration-300 ease-in-out z-[1000] overflow-y-auto ${
        isCollapsed
          ? 'w-0 min-w-0 max-w-0 overflow-hidden border-0 p-0 shadow-none bg-transparent pointer-events-none'
          : 'w-[20%] max-w-[400px] min-w-[280px]'
      }`}
      style={{ pointerEvents: 'auto' }}
      data-tour-id={dataTourId}
      onClick={(e) => {
        if (!isCollapsed) {
          e.stopPropagation();
          disableUnityCapture();
        }
      }}
    >
      {/* Loading Overlay */}
      {isLoading && !isCollapsed && (
        <div className="unity-ivs-glass-loading absolute inset-0 flex flex-col items-center justify-center z-[1002]">
          <FaSpinner className="animate-spin text-blue-500 mb-4" size={48} />
          <div className="text-white text-lg font-semibold mb-2">Switching Rooms</div>
          <div className="text-[#999] text-sm">Please wait...</div>
        </div>
      )}

      {!isCollapsed && (
        <div className="flex flex-col h-full p-4 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-white/25">
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-white/10">
            <div className="flex flex-col gap-1">
              <h3 className="text-white text-lg font-semibold m-0">{roomName}</h3>
            </div>
            <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-[#999]">
              <span 
                className={`w-2 h-2 rounded-full ${
                  isConnected 
                    ? 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.5)]' 
                    : 'bg-yellow-500'
                }`}
              ></span>
              <span>
                {isConnected 
                  ? 'Connected' 
                  : 'Connecting...'}
              </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCollapseToggle();
                }}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                title="Minimize to dock"
              >
                <FaChevronRight size={16} aria-hidden />
              </button>
            </div>
          </div>

          {/* Video Control */}
          <div className="mb-4 pb-4 border-b border-white/10">
            {/* Video calls not available message */}
            {!isVideoCallAvailable && !isFetchingVideoCallToken && (
              <div className="px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-gray-400 text-sm text-center mb-2">
                <FaVideoSlash className="inline-block mr-2" />
                Video calls not enabled for this room
              </div>
            )}
            
            {/* Loading video call token */}
            {isFetchingVideoCallToken && (
              <div className="px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-gray-400 text-sm text-center mb-2 flex items-center justify-center gap-2">
                <FaSpinner className="animate-spin" size={14} />
                <span>Connecting to video call...</span>
              </div>
            )}
            
            <div className="flex flex-col gap-2">
              {/* Camera Controls Row - Only show if video calls are available */}
              {isVideoCallAvailable && (
              <div className="flex flex-col gap-1.5">
                <div className="flex gap-2">
                {localVideoEnabled ? (
                  <button
                    className="flex-1 p-2.5 rounded-md text-white cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 text-base bg-blue-500 border border-blue-500 hover:bg-blue-600"
                    onClick={toggleLocalVideo}
                    disabled={!isVideoCallConnected}
                    title="Turn off camera"
                  >
                    <FaVideo />
                    <span className="text-sm font-medium">Stop Camera</span>
                  </button>
                ) : (
                  <button
                    className={`flex-1 p-2.5 rounded-md text-white cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 text-base bg-[#2a2a2a] border border-[#444] hover:bg-[#3a3a3a] hover:border-[#555] ${!videoFeedsEnabled || !isVideoCallConnected || isAtVideoLimit || isFetchingVideoCallToken ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={openPreviewModal}
                    disabled={!videoFeedsEnabled || !isVideoCallConnected || isAtVideoLimit || isFetchingVideoCallToken}
                    title={!videoFeedsEnabled ? 'Turn on video feeds first' : !isVideoCallConnected ? 'Video call not connected' : isAtVideoLimit ? 'Video limit reached' : isFetchingVideoCallToken ? 'Connecting...' : 'Preview and start camera'}
                  >
                    <FaEye />
                    <span className="text-sm font-medium">Preview Camera</span>
                  </button>
                )}
                </div>
                {!localVideoEnabled && (
                  <div className="px-1 text-center text-[11px] leading-4 text-white/45">
                    Preview your camera before it appears above your avatar.
                  </div>
                )}
              </div>
              )}
              
              {segmenterError && (
                <div className="px-2 py-1.5 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-xs text-center">
                  {segmenterError}
                </div>
              )}
            </div>

            {screenShareControls}

            {/* Video limit warning */}
            {!localVideoEnabled && isAtVideoLimit && (
              <div className="px-2 py-1.5 mt-2 bg-orange-900/30 border border-orange-700/50 rounded text-orange-300 text-xs text-center">
                Video limit reached ({totalPublishers}/{MAX_VIDEO_PARTICIPANTS} publishers)
              </div>
            )}
          </div>

          {/* Debug Controls */}
          {debugMode && (
            <div className="mb-4 pb-4 border-b border-white/10">
              <div className="text-yellow-400 text-xs font-semibold mb-2 uppercase">Debug Mode</div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
            <button
                    onClick={addDebugParticipant}
                    disabled={isAtVideoLimit}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isAtVideoLimit ? 'Video limit reached' : 'Add a mock participant'}
                  >
                    + Add Participant
                  </button>
                  <button
                    onClick={removeDebugParticipant}
                    disabled={debugParticipants.length === 0}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Remove last participant"
                  >
                    - Remove
            </button>
          </div>
                <button
                  onClick={clearDebugParticipants}
                  disabled={debugParticipants.length === 0}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Clear all participants"
                >
                  Clear All ({debugParticipants.length})
                </button>
                {isAtVideoLimit && (
                  <div className="px-2 py-1 bg-yellow-900/30 border border-yellow-700/50 rounded text-yellow-300 text-xs text-center">
                    Max {MAX_VIDEO_PARTICIPANTS} participants reached
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-1 flex-col items-center justify-start gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-5 text-center">
            <div className={`flex h-12 w-12 items-center justify-center rounded-full border ${videoFeedsEnabled ? 'border-violet-300/40 bg-violet-500/15 text-violet-200' : 'border-white/15 bg-white/5 text-white/40'}`}>
              {videoFeedsEnabled ? <FaVideo size={20} /> : <FaVideoSlash size={20} />}
            </div>
            <div className="text-sm font-medium text-white">
              {videoFeedsEnabled ? 'Video now lives in the world' : 'Video feeds are off'}
            </div>
            <div className="text-xs leading-5 text-white/50">
              {videoFeedsEnabled
                ? 'Nearby camera feeds appear between avatar heads and name tags.'
                : "You won't see nearby cameras, and your camera is not being shared."}
            </div>
            {localVideoEnabled && (
              <div className="rounded-full border border-emerald-300/30 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200">
                {localVideoLoading ? 'Starting your camera…' : 'You are currently sharing your camera.'}
              </div>
            )}
            <div className="text-[11px] text-white/40">
              {videoFeedsEnabled ? participantStreams.size : 0} nearby {videoFeedsEnabled && participantStreams.size === 1 ? 'feed' : 'feeds'} active
              {screenShareActive ? ' · screen sharing' : ''}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={videoFeedsEnabled}
              onClick={() => void toggleVideoFeeds()}
              className="mt-1 flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-left transition-colors hover:bg-white/10"
            >
              <span>
                <span className="block text-xs font-medium text-white">Video feeds</span>
                <span className="block text-[10px] text-white/40">Show cameras near your avatar</span>
              </span>
              <span className={`relative h-5 w-9 rounded-full transition-colors ${videoFeedsEnabled ? 'bg-violet-500' : 'bg-white/20'}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${videoFeedsEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
              </span>
            </button>
          </div>
        </div>
      )}
    </div>

      {inWorldVideoEnabled && videoFeedsEnabled && typeof document !== 'undefined' && (() => {
        const root = document.getElementById('unity-game-face-pins-root');
        return root
          ? createPortal(
              <AvatarFacePins
                participantStreams={participantStreams}
                participants={participants}
                localUserId={userId}
                localStream={localVideoEnabled ? localStream : null}
              />,
              root
            )
          : null;
      })()}

      {/* Preview modal: portal to body so it stacks above the Unity dock (z-index 10000) */}
      {showPreviewModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="unity-custom-cursor fixed inset-0 z-[10050] flex items-center justify-center bg-black/80"
            style={{ pointerEvents: 'auto' }}
            onClick={closePreviewModal}
          >
            <div
              className="unity-ivs-glass-modal-card w-[90%] max-w-[600px] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <h2 className="text-white text-lg font-semibold">Camera Preview</h2>
                <button
                  onClick={closePreviewModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-[#333] rounded-lg transition-colors"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              {/* Preview Video Area */}
              <div className="relative aspect-video bg-black m-4 rounded-lg overflow-hidden">
                {previewEffect !== 'none' ? (
                  <canvas
                    ref={previewCanvasRef}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <video
                    ref={previewVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Hidden video for effect processing */}
                {previewEffect !== 'none' && (
                  <video
                    ref={previewVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="hidden"
                  />
                )}

                {isSegmenterLoading && previewEffect !== 'none' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <div className="flex flex-col items-center gap-2 text-white">
                      <FaSpinner className="animate-spin" size={32} />
                      <span className="text-sm">Loading effect...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Effect Selection */}
              <div className="px-5 pb-4">
                <div className="text-gray-400 text-sm mb-3">Choose background:</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      previewEffect === 'none'
                        ? 'border-blue-500 bg-blue-500/20 text-white'
                        : 'border-[#444] bg-[#2a2a2a] text-gray-300 hover:border-[#555] hover:bg-[#333]'
                    }`}
                    onClick={() => setPreviewEffect('none')}
                  >
                    <FaVideo size={20} />
                    <span className="text-sm font-medium">Normal</span>
                  </button>
                  <button
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      previewEffect === 'blur'
                        ? 'border-purple-500 bg-purple-500/20 text-white'
                        : 'border-[#444] bg-[#2a2a2a] text-gray-300 hover:border-[#555] hover:bg-[#333]'
                    }`}
                    onClick={() => setPreviewEffect('blur')}
                  >
                    <FaUserCircle size={20} />
                    <span className="text-sm font-medium">Blur</span>
                  </button>
                  <button
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 relative overflow-hidden ${
                      previewEffect === 'virtual-neon'
                        ? 'border-cyan-500 ring-2 ring-cyan-500/50'
                        : 'border-[#444] hover:border-[#555]'
                    }`}
                    onClick={() => setPreviewEffect('virtual-neon')}
                  >
                    <img src={virtualBgNeon} alt="Neon" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                    <span className="relative text-sm font-medium text-white drop-shadow-lg z-10">Neon</span>
                  </button>
                  <button
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 relative overflow-hidden ${
                      previewEffect === 'intersect-background'
                        ? 'border-pink-500 ring-2 ring-pink-500/50'
                        : 'border-[#444] hover:border-[#555]'
                    }`}
                    onClick={() => setPreviewEffect('intersect-background')}
                  >
                    <img src={intersectBg} alt="Intersect Background" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                    <span className="relative text-sm font-medium text-white drop-shadow-lg z-10">Intersect</span>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 px-5 py-4 bg-black/25 border-t border-white/10">
                <button
                  onClick={closePreviewModal}
                  className="flex-1 py-3 px-4 rounded-lg bg-[#333] text-white hover:bg-[#444] transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={applyPreviewAndStart}
                  disabled={isSegmenterLoading}
                  className="flex-1 py-3 px-4 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <FaCheck />
                  Start Camera
                </button>
              </div>
            </div>
          </div>,
          getFullscreenPortalTarget()
        )}
    </>
  );
  void renderLegacyPanel;

  const closeVideoDock = () => {
    closePreviewModal();
    setIsCollapsed(true);
    onCollapseChange?.(true);
  };

  const toggleVideoDock = () => {
    if (!isCollapsed) {
      closeVideoDock();
      return;
    }
    setIsCollapsed(false);
    onCollapseChange?.(false);
    if (!localVideoEnabled && videoFeedsEnabled && !showPreviewModal) {
      void openPreviewModal();
    }
  };

  const videoPopover = !isCollapsed ? (
    <div
      className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide flex max-h-[min(85vh,38rem)] w-[min(92vw,32rem)] flex-col overflow-hidden"
      onClick={(event) => {
        event.stopPropagation();
        disableUnityCapture();
      }}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div>
          <h3 className="text-base font-semibold text-white">Video</h3>
          <p className="text-[11px] text-white/70">
            {isConnected ? `${participantStreams.size} nearby camera feeds` : 'Connecting to video…'}
          </p>
        </div>
        <button type="button" onClick={closeVideoDock} className="rounded p-2 text-white/50 hover:bg-white/10 hover:text-white" aria-label="Close Video">
          <FaTimes size={17} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {!localVideoEnabled && (isLoading || isFetchingVideoCallToken) ? (
          <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-white/75">
            <FaSpinner className="animate-spin text-violet-400" size={28} />
            <span className="text-sm">Preparing video…</span>
          </div>
        ) : !localVideoEnabled && !isVideoCallAvailable ? (
          <div className="rounded-xl border border-white/15 bg-white/5 p-5 text-center text-sm text-white/75">
            Video is not available in this room.
          </div>
        ) : localVideoEnabled ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-300/25 bg-emerald-500/10 p-4 text-center">
              <div className="mx-auto mb-2 h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-400" />
              <div className="text-sm font-medium text-emerald-100">
                {localVideoLoading ? 'Starting your camera…' : isVideoCallConnected ? 'You are currently sharing your camera.' : 'Camera stays on. Sharing resumes when room video connects.'}
              </div>
              <div className="mt-1 text-[11px] text-emerald-50/80">Your face pin appears over your avatar's face.</div>
            </div>
            <button
              type="button"
              onClick={() => void toggleLocalVideo()}
              disabled={localVideoLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-500/90 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
            >
              <FaVideoSlash /> Stop sharing camera
            </button>
          </div>
        ) : showPreviewModal ? (
          <div className="space-y-4">
            <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
              {previewEffect !== 'none' ? (
                <canvas ref={previewCanvasRef} className="h-full w-full object-cover" />
              ) : (
                <video ref={previewVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
              )}
              {previewEffect !== 'none' && <video ref={previewVideoRef} autoPlay playsInline muted className="hidden" />}
              {isSegmenterLoading && previewEffect !== 'none' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white">
                  <FaSpinner className="animate-spin" size={28} />
                </div>
              )}
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-white/80">Background</div>
              <div className="grid grid-cols-4 gap-2">
                {([
                  ['none', 'Normal'],
                  ['blur', 'Blur'],
                  ['virtual-neon', 'Neon'],
                  ['intersect-background', 'Intersect'],
                ] as Array<[BackgroundEffect, string]>).map(([effect, label]) => (
                  <button
                    key={effect}
                    type="button"
                    onClick={() => setPreviewEffect(effect)}
                    className={`rounded-lg border px-2 py-2 text-xs transition-colors ${previewEffect === effect ? 'border-violet-400 bg-violet-500/20 text-white' : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10 hover:text-white'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {segmenterError && <div className="rounded-lg border border-red-400/25 bg-red-500/10 p-2 text-center text-xs text-red-200">{segmenterError}</div>}
            <button
              type="button"
              onClick={() => void applyPreviewAndStart()}
              disabled={isSegmenterLoading || !previewStream || !isVideoCallConnected || isAtVideoLimit}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              <FaCheck /> Share camera
            </button>
          </div>
        ) : (
          <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center text-white/75">
            <FaVideoSlash size={28} />
            <span className="text-sm">Camera preview is unavailable.</span>
          </div>
        )}

        <button
          type="button"
          role="switch"
          aria-checked={videoFeedsEnabled}
          onClick={() => void toggleVideoFeeds()}
          className="mt-4 flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-left hover:bg-white/10"
        >
          <span>
            <span className="block text-xs font-medium text-white">Nearby video feeds</span>
            <span className="block text-[10px] text-white/70">Show face pins within 8 meters</span>
          </span>
          <span className={`relative h-5 w-9 rounded-full transition-colors ${videoFeedsEnabled ? 'bg-violet-500' : 'bg-white/20'}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${videoFeedsEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
          </span>
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <UnityGameDockApp
        dataTourId={dataTourId || 'tour-video-dock'}
        ariaLabel="Video — preview and share your camera"
        label="Video"
        icon={localVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
        badge={localVideoEnabled ? <span className="block h-2 w-2 rounded-full bg-emerald-300" /> : undefined}
        active={!isCollapsed}
        onRequestClose={closeVideoDock}
        tileClassName="bg-gradient-to-br from-violet-600 to-fuchsia-800"
        onClick={(event) => {
          event.stopPropagation();
          toggleVideoDock();
        }}
      >
        {videoPopover}
      </UnityGameDockApp>

      {inWorldVideoEnabled && videoFeedsEnabled && typeof document !== 'undefined' && (() => {
        const root = document.getElementById('unity-game-face-pins-root');
        return root ? createPortal(
          <AvatarFacePins
            participantStreams={participantStreams}
            participants={participants}
            localUserId={userId}
            localStream={localVideoEnabled ? localStream : null}
          />,
          root
        ) : null;
      })()}
    </>
  );
};

export default IVSRealtimePanel;
